/**
 * Product Detail Section (Aqua) - JavaScript
 * Handles thumbnail navigation, quantity selector, and cart actions
 */

(function() {
  'use strict';

  class ProductDetailSection {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;

      // Elements
      this.mainImage = section.querySelector(`#MainImage-${this.sectionId}`);
      this.thumbnails = section.querySelectorAll('[data-thumbnail]');
      this.thumbnailsContainer = section.querySelector('[data-thumbnails]');
      this.prevBtn = section.querySelector('[data-thumb-prev]');
      this.nextBtn = section.querySelector('[data-thumb-next]');

      this.qtyInput = section.querySelector('[data-qty-input]');
      this.qtyMinus = section.querySelector('[data-qty-minus]');
      this.qtyPlus = section.querySelector('[data-qty-plus]');

      this.variantInput = section.querySelector('[data-variant-id]');
      this.addToCartBtn = section.querySelector('[data-add-to-cart]');
      this.buyNowBtn = section.querySelector('[data-buy-now]');

      this.currentIndex = 0;
      this.isLoading = false;

      // Store original button text
      this.addToCartOriginalText = this.addToCartBtn ? this.addToCartBtn.textContent.trim() : 'ADD TO CART';
      this.buyNowOriginalText = this.buyNowBtn ? this.buyNowBtn.textContent.trim() : 'BUY NOW';

      this.init();
    }

    init() {
      this.bindThumbnailEvents();
      this.bindQuantityEvents();
      this.bindCartEvents();
      this.bindNavigationEvents();
      this.updateNavigationState();
      this.updateMinusButtonState(1);

      // Recalculate on resize
      window.addEventListener('resize', () => {
        this.updateNavigationState();
      });
    }

    // Thumbnail Gallery
    bindThumbnailEvents() {
      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => {
          this.selectThumbnail(index);
        });
      });
    }

    selectThumbnail(index) {
      // Update active state
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('product-detail-section__thumbnail--active', i === index);
      });

      // Update main image
      const selectedThumbnail = this.thumbnails[index];
      if (selectedThumbnail && this.mainImage) {
        const newImageUrl = selectedThumbnail.dataset.imageUrl;
        if (newImageUrl) {
          this.mainImage.src = newImageUrl;
          this.mainImage.srcset = `${newImageUrl.replace('width=1164', 'width=582')} 582w, ${newImageUrl} 1164w`;
        }
      }

      this.currentIndex = index;
    }

    bindNavigationEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.navigateThumbnails('prev');
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.navigateThumbnails('next');
        });
      }
    }

    navigateThumbnails(direction) {
      const totalThumbnails = this.thumbnails.length;

      if (direction === 'prev') {
        this.currentIndex = Math.max(0, this.currentIndex - 1);
      } else {
        this.currentIndex = Math.min(totalThumbnails - 1, this.currentIndex + 1);
      }

      this.selectThumbnail(this.currentIndex);
      this.scrollToThumbnail(this.currentIndex);
      this.updateNavigationState();
    }

    scrollToThumbnail(index) {
      if (!this.thumbnailsContainer || !this.thumbnails[index]) return;

      const thumbnail = this.thumbnails[index];
      const containerRect = this.thumbnailsContainer.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();

      const scrollLeft = thumbnail.offsetLeft - (containerRect.width / 2) + (thumbnailRect.width / 2);

      this.thumbnailsContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }

    updateNavigationState() {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentIndex === 0;
        this.prevBtn.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
      }

      if (this.nextBtn) {
        const isLast = this.currentIndex >= this.thumbnails.length - 1;
        this.nextBtn.disabled = isLast;
        this.nextBtn.style.opacity = isLast ? '0.3' : '1';
      }
    }

    // Quantity Selector
    bindQuantityEvents() {
      if (this.qtyMinus) {
        this.qtyMinus.addEventListener('click', () => {
          this.updateQuantity(-1);
        });
      }

      if (this.qtyPlus) {
        this.qtyPlus.addEventListener('click', () => {
          this.updateQuantity(1);
        });
      }

      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => {
          this.validateQuantity();
        });

        this.qtyInput.addEventListener('blur', () => {
          this.validateQuantity();
        });

        // Prevent negative values on key press
        this.qtyInput.addEventListener('keydown', (e) => {
          if (e.key === '-' || e.key === 'e') {
            e.preventDefault();
          }
        });
      }
    }

    updateQuantity(change) {
      if (!this.qtyInput) return;

      let currentValue = parseInt(this.qtyInput.value, 10) || 1;
      let newValue = currentValue + change;

      newValue = Math.max(1, newValue);

      this.qtyInput.value = newValue;
      this.updateMinusButtonState(newValue);
    }

    validateQuantity() {
      if (!this.qtyInput) return;

      let value = parseInt(this.qtyInput.value, 10);

      if (isNaN(value) || value < 1) {
        value = 1;
      }

      this.qtyInput.value = value;
      this.updateMinusButtonState(value);
    }

    updateMinusButtonState(value) {
      if (this.qtyMinus) {
        this.qtyMinus.disabled = value <= 1;
        this.qtyMinus.style.opacity = value <= 1 ? '0.3' : '1';
      }
    }

    // Cart Actions
    bindCartEvents() {
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this.isLoading && !this.addToCartBtn.disabled) {
            this.addToCart();
          }
        });
      }

      if (this.buyNowBtn) {
        this.buyNowBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this.isLoading && !this.buyNowBtn.disabled) {
            this.buyNow();
          }
        });
      }
    }

    getVariantId() {
      if (this.variantInput) {
        return this.variantInput.value;
      }
      return null;
    }

    getQuantity() {
      return this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
    }

    setLoadingState(isLoading, button, loadingText) {
      this.isLoading = isLoading;

      if (button) {
        button.disabled = isLoading;
        if (isLoading) {
          button.textContent = loadingText;
          button.style.opacity = '0.7';
        } else {
          button.style.opacity = '1';
        }
      }
    }

    addToCart() {
      const variantId = this.getVariantId();
      const quantity = this.getQuantity();

      if (!variantId) {
        this.showNotification('Please select a product', 'error');
        return;
      }

      this.setLoadingState(true, this.addToCartBtn, 'ADDING...');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: quantity
        })
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => { throw err; });
        }
        return response.json();
      })
      .then(data => {
        // Success
        this.addToCartBtn.textContent = 'ADDED!';

        // Dispatch custom event for cart drawer/notification
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item: data, quantity: quantity }
        }));

        // Update cart count in header if exists
        this.updateCartCount();

        // Show success notification
        this.showNotification(`${data.title} added to cart!`, 'success');

        // Reset button after delay
        setTimeout(() => {
          this.setLoadingState(false, this.addToCartBtn);
          this.addToCartBtn.textContent = this.addToCartOriginalText;
        }, 2000);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        this.setLoadingState(false, this.addToCartBtn);
        this.addToCartBtn.textContent = this.addToCartOriginalText;

        const errorMessage = error.description || error.message || 'Could not add to cart. Please try again.';
        this.showNotification(errorMessage, 'error');
      });
    }

    buyNow() {
      const variantId = this.getVariantId();
      const quantity = this.getQuantity();

      if (!variantId) {
        this.showNotification('Please select a product', 'error');
        return;
      }

      this.setLoadingState(true, this.buyNowBtn, 'PROCESSING...');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: quantity
        })
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => { throw err; });
        }
        return response.json();
      })
      .then(() => {
        // Redirect to checkout
        window.location.href = '/checkout';
      })
      .catch(error => {
        console.error('Error during buy now:', error);
        this.setLoadingState(false, this.buyNowBtn);
        this.buyNowBtn.textContent = this.buyNowOriginalText;

        const errorMessage = error.description || error.message || 'Could not process. Please try again.';
        this.showNotification(errorMessage, 'error');
      });
    }

    updateCartCount() {
      fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })
      .then(response => response.json())
      .then(cart => {
        // Dispatch event with cart data for header to update
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cart, itemCount: cart.item_count }
        }));

        // Try to update common cart count selectors
        const cartCountElements = document.querySelectorAll(
          '[data-cart-count], .cart-count, .cart-count-bubble, #cart-icon-bubble span, .header__cart-count'
        );
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.style.display = '';
          }
        });
      })
      .catch(error => {
        console.error('Error updating cart count:', error);
      });
    }

    showNotification(message, type = 'success') {
      // Remove existing notifications
      const existingNotification = document.querySelector('.product-detail-notification');
      if (existingNotification) {
        existingNotification.remove();
      }

      // Create notification element
      const notification = document.createElement('div');
      notification.className = `product-detail-notification product-detail-notification--${type}`;
      notification.innerHTML = `
        <span class="product-detail-notification__message">${message}</span>
        <button class="product-detail-notification__close" aria-label="Close notification">&times;</button>
      `;

      // Add styles if not already present
      if (!document.querySelector('#product-detail-notification-styles')) {
        const styles = document.createElement('style');
        styles.id = 'product-detail-notification-styles';
        styles.textContent = `
          .product-detail-notification {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 16px 20px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            gap: 12px;
            z-index: 9999;
            font-family: 'Manrope', sans-serif;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            animation: slideIn 0.3s ease;
            max-width: 400px;
          }
          .product-detail-notification--success {
            background-color: #22C55E;
            color: white;
          }
          .product-detail-notification--error {
            background-color: #EF4444;
            color: white;
          }
          .product-detail-notification__close {
            background: none;
            border: none;
            color: white;
            font-size: 20px;
            cursor: pointer;
            padding: 0;
            line-height: 1;
            opacity: 0.8;
          }
          .product-detail-notification__close:hover {
            opacity: 1;
          }
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes slideOut {
            from {
              transform: translateX(0);
              opacity: 1;
            }
            to {
              transform: translateX(100%);
              opacity: 0;
            }
          }
        `;
        document.head.appendChild(styles);
      }

      document.body.appendChild(notification);

      // Close button handler
      const closeBtn = notification.querySelector('.product-detail-notification__close');
      closeBtn.addEventListener('click', () => {
        this.removeNotification(notification);
      });

      // Auto-remove after 4 seconds
      setTimeout(() => {
        this.removeNotification(notification);
      }, 4000);
    }

    removeNotification(notification) {
      if (notification && notification.parentNode) {
        notification.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => {
          if (notification.parentNode) {
            notification.remove();
          }
        }, 300);
      }
    }
  }

  // Initialize all product detail sections
  function initProductDetailSections() {
    const sections = document.querySelectorAll('.product-detail-section');
    sections.forEach(section => {
      // Prevent double initialization
      if (!section.dataset.initialized) {
        new ProductDetailSection(section);
        section.dataset.initialized = 'true';
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductDetailSections);
  } else {
    initProductDetailSections();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.product-detail-section');
    if (section) {
      section.dataset.initialized = '';
      new ProductDetailSection(section);
      section.dataset.initialized = 'true';
    }
  });
})();
